<template>
  <div class="nset" dir="rtl" v-if="page_loaded" :key="profile.id">

    <!-- ── Inline error banner ── -->
    <div v-if="errorMsg" class="nset__banner nset__banner--error" role="alert" aria-live="assertive">
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true">
        <circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/>
      </svg>
      <span>{{ errorMsg }}</span>
    </div>

    <!-- ── Step indicator ── -->
    <div class="nset__steps" role="progressbar" :aria-valuenow="step" aria-valuemin="1" aria-valuemax="4" :aria-label="'שלב ' + step + ' מתוך 4'">
      <span
        v-for="n in 4"
        :key="n"
        class="nset__step-dot"
        :class="{ 'nset__step-dot--active': n === step, 'nset__step-dot--done': n < step }"
        :aria-hidden="true"
      ></span>
    </div>

    <div class="nset__page">

      <!-- ── Step 1: שם פרופיל ── -->
      <div class="nset__card" v-if="step === 1">
        <h2 class="nset__heading">1/4 — שם הפרופיל</h2>
        <p class="nset__body">
          של מי המנוי? נשמח לשמוע איך קוראים לכם<br>
          <span class="nset__body-sub">אפשר לשנות את השם בכל עת מהגדרות הפרופיל</span>
        </p>

        <ui-input
          label="השם שלך"
          placeholder="הקלד שם"
          v-model="profile.name"
          :required="true"
          class="nset__input"
        />

        <div class="nset__actions nset__actions--end">
          <ui-button variant="primary" size="lg" @click="goToStep2">המשך</ui-button>
        </div>
      </div>

      <!-- ── Step 2: רמת הגנה ── -->
      <div class="nset__card" v-if="step === 2">
        <h2 class="nset__heading">2/4 — רמת סינון תוכן</h2>
        <p class="nset__body">בחר את רמת ההגנה והסינון שמתאימה לך.</p>

        <div class="nset__rest-grid">
          <label
            v-for="item in restrictions_list"
            :key="item.id"
            class="nset__rest-card"
            :class="{ 'nset__rest-card--active': profile.plan_id === item.id }"
          >
            <input
              type="radio"
              :value="item.id"
              v-model="profile.plan_id"
              class="nset__radio-native"
              :aria-label="item.name"
            />
            <!-- TODO: item.icon_url was a CSS class (e.g. "icon-family"); replaced with generic profile SVG pending icon migration -->
            <svg class="nset__rest-icon" width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
              <circle cx="12" cy="8" r="4"/>
              <path d="M4 20c0-4 3.6-7 8-7s8 3 8 7"/>
            </svg>
            <span class="nset__rest-name">{{ item.name }}</span>
            <span class="nset__rest-desc">{{ item.details }}</span>
          </label>
        </div>

        <div class="nset__actions">
          <ui-button variant="ghost" size="lg" @click="step = 1">חזרה</ui-button>
          <ui-button variant="primary" size="lg" @click="profile.has_perms = true; step = 3">המשך</ui-button>
        </div>
      </div>

      <!-- ── Step 3: ניהול התראות ── -->
      <div class="nset__card" v-if="step === 3">
        <h2 class="nset__heading">3/4 — ניהול התראות</h2>
        <p class="nset__body">אם יהיו במהלך הסרט קטעים בעייתיים, מה תרצו שנעשה?</p>

        <div class="nset__option-group" role="radiogroup" aria-label="אפשרויות ניהול תוכן">
          <label
            class="nset__option-card"
            :class="{ 'nset__option-card--active': stage === 1 }"
          >
            <input
              type="radio"
              name="content_mode"
              class="nset__radio-native"
              :checked="stage === 1"
              @change="show_skip = true; stage = 1; show_alert = false"
              aria-label="דלגו עליהם אוטומטית"
            />
            <!-- skip: heart-fill icon -->
            <svg class="nset__option-icon" width="28" height="28" viewBox="0 0 24 24" fill="currentColor" stroke="none" aria-hidden="true"><path d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z"/></svg>
            <span class="nset__option-label">הכי טוב שתדלגו עליהם</span>
          </label>

          <label
            class="nset__option-card"
            :class="{ 'nset__option-card--active': stage === 2 }"
          >
            <input
              type="radio"
              name="content_mode"
              class="nset__radio-native"
              :checked="stage === 2"
              @change="show_alert = true; stage = 2; show_skip = false"
              aria-label="הצג התראה ואני אחליט"
            />
            <!-- alert: smile icon -->
            <svg class="nset__option-icon" width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><circle cx="12" cy="12" r="10"/><path d="M8 13s1.5 2 4 2 4-2 4-2"/><line x1="9" y1="9" x2="9.01" y2="9"/><line x1="15" y1="9" x2="15.01" y2="9"/></svg>
            <span class="nset__option-label">תקפיצו התראה ואחליט בעצמי</span>
          </label>

          <label
            class="nset__option-card"
            :class="{ 'nset__option-card--active': stage === 3 }"
          >
            <input
              type="radio"
              name="content_mode"
              class="nset__radio-native"
              :checked="stage === 3"
              @change="stage = 3; show_skip = false; show_alert = false"
              aria-label="המשיכו כרגיל"
            />
            <!-- continue: neutral circle icon -->
            <svg class="nset__option-icon" width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><circle cx="12" cy="12" r="10"/><line x1="8" y1="12" x2="16" y2="12"/><line x1="9" y1="9" x2="9.01" y2="9"/><line x1="15" y1="9" x2="15.01" y2="9"/></svg>
            <span class="nset__option-label">תמשיכו כרגיל</span>
          </label>
        </div>

        <div class="nset__actions">
          <ui-button variant="ghost" size="lg" @click="step = 2">חזרה</ui-button>
          <ui-button variant="primary" size="lg" @click="step = 4">המשך</ui-button>
        </div>
      </div>

      <!-- ── Step 4: PIN / סיסמה ── -->
      <div class="nset__card" v-if="step === 4">
        <h2 class="nset__heading">4/4 — קוד PIN</h2>
        <p class="nset__body">
          תוכלו ליצור קוד PIN לנעילת הפרופיל.<br>
          <span class="nset__body-sub">השאר ריק אם אינך רוצה נעילת PIN</span>
        </p>

        <!-- PIN display cells -->
        <div class="nset__pin-group">
          <span class="nset__pin-label">PIN (4 ספרות, אופציונלי)</span>
          <div class="nset__pin-row" role="group" aria-label="ספרות PIN">
            <div
              v-for="(digit, idx) in pinDigits"
              :key="'pin' + idx"
              class="nset__pin-cell"
              :class="{ 'nset__pin-cell--filled': digit !== '' }"
              aria-hidden="true"
            >{{ digit !== '' ? '•' : '' }}</div>
          </div>
          <!-- Hidden real input -->
          <input
            type="password"
            maxlength="4"
            inputmode="numeric"
            pattern="[0-9]*"
            v-model="profile.password"
            class="nset__pin-hidden"
            placeholder="הקלד 4 ספרות"
            autocomplete="new-password"
            aria-label="קוד PIN בן 4 ספרות"
            @input="syncPinDigits"
          />
          <p class="nset__pin-hint">השאר ריק אם אינך רוצה נעילת PIN</p>
        </div>

        <div class="nset__actions">
          <ui-button variant="ghost" size="lg" @click="step = 3">חזרה</ui-button>
          <ui-button variant="primary" size="lg" :loading="saving" @click="save">סיים הגדרה</ui-button>
        </div>
      </div>

    </div>
  </div>
</template>

<script>
import UiButton from './ui/Button.vue';
import UiInput  from './ui/Input.vue';

export default {
  components: { UiButton, UiInput },

  data() {
    return {
      page_loaded: false,
      step: 1,
      stage: 1,
      id: this.$route.params.id,
      profile: false,
      restrictions: [],
      saved_restrictions: [],
      insert_mode: false,
      show_alert: true,
      has_perms: true,
      show_skip: true,
      saving: false,
      errorMsg: '',
      pinDigits: ['', '', '', ''],
    };
  },

  computed: {
    restrictions_list() {
      var restrictions = JSON.parse(JSON.stringify(this.$store.state.restrictions_plans));
      for (let i in restrictions) {
        restrictions[i].is_active = (this.saved_restrictions.indexOf(restrictions[i].id) !== -1);
      }
      return restrictions;
    },
  },

  methods: {
    goToStep2() {
      if (!this.profile.name || this.profile.name.trim() === '') {
        this.errorMsg = 'יש להזין שם פרופיל לפני שממשיכים';
        return;
      }
      this.errorMsg = '';
      this.step = 2;
    },

    syncPinDigits() {
      var val = (this.profile.password || '').split('');
      this.pinDigits = [
        val[0] || '',
        val[1] || '',
        val[2] || '',
        val[3] || '',
      ];
    },

    save() {
      this.saving = true;
      this.errorMsg = '';
      this.api_call({
        url: 'save_profile',
        data: {
          insert_mode: false,
          id: this.id,
          name: this.profile.name,
          age: this.profile.age,
          plan_id: this.profile.plan_id,
          limit_hours: this.profile.limit_hours,
          password: this.profile.password,
          perms_show_alerts: this.show_alert,
          perms_auto_skip: this.show_skip,
          perms_block_items: (this.profile.perms_block_items ? 1 : 0),
          has_perms: this.has_perms,
          new_setup: (this.profile.new_setup ? 0 : 1),
        },
        onComplete: () => {
          this.saving = false;
          document.location = '/';
        },
      });
    },

    remove_profile() {
      // replace confirm() with inline state — show a confirmation step if needed
      this.api_call({
        url: 'remove_profile',
        data: { id: this.id },
        onComplete: () => {
          document.location = '/user/profiles';
        },
      });
    },
  },

  mounted() {
    if (!(this.id > 0)) {
      document.location = '/';
      return;
    }

    this.api_call({
      url: 'get_profile',
      data: { id: this.id },
      onComplete: (data) => {
        data.data.data.perms_show_alerts  = (data.data.data.perms_show_alerts == 1);
        data.data.data.perms_auto_skip    = (data.data.data.perms_auto_skip == 1);
        data.data.data.perms_block_items  = (data.data.data.perms_block_items == 1);
        this.profile = data.data.data;
        this.page_loaded = true;
        this.saved_restrictions = data.data.data.restrictions;
        this.syncPinDigits();
      },
    });
  },
};
</script>

<style scoped lang="scss">
/* ── Root ── */
.nset {
  min-height: 100vh;
  background: var(--color-bg);
  font-family: var(--font-hebrew);
  padding-block: var(--space-12);
}

/* ── Step indicator ── */
.nset__steps {
  display: flex;
  justify-content: center;
  gap: var(--space-2);
  margin-block-end: var(--space-8);
}

.nset__step-dot {
  width: 8px;
  height: 8px;
  border-radius: var(--radius-pill);
  background: var(--color-surface3);
  transition: all var(--duration-base) var(--easing-default);
}

.nset__step-dot--active {
  width: 24px;
  background: var(--color-accent);
}

.nset__step-dot--done {
  background: var(--color-surface4);
}

/* ── Page container ── */
.nset__page {
  max-width: 560px;
  margin-inline: auto;
  padding-inline: var(--space-6);
}

/* ── Card ── */
.nset__card {
  background: var(--color-surface1);
  border: 1px solid var(--color-border);
  border-radius: var(--radius-md);
  padding: var(--space-8);
  display: flex;
  flex-direction: column;
  gap: var(--space-6);
}

/* ── Typography ── */
.nset__heading {
  font-size: 22px;
  font-weight: 600;
  color: var(--color-text);
  letter-spacing: -0.2px;
  margin: 0;
}

.nset__body {
  font-size: 15px;
  color: var(--color-textMuted);
  line-height: 1.6;
  margin: 0;
}

.nset__body-sub {
  font-size: 13px;
  color: var(--color-textMuted);
}

/* ── Input override (forward Hebrew font) ── */
.nset__input {
  --font-sans: var(--font-hebrew);
}

/* ── Restrictions grid ── */
.nset__rest-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(130px, 1fr));
  gap: var(--space-3);
}

.nset__rest-card {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: var(--space-2);
  padding: var(--space-4);
  background: var(--color-surface2);
  border: 1px solid var(--color-border);
  border-radius: var(--radius-md);
  cursor: pointer;
  text-align: center;
  transition: border-color var(--duration-base) var(--easing-default),
              background var(--duration-base) var(--easing-default);
}

.nset__rest-card--active {
  border-color: var(--color-accent);
  background: rgba(225, 29, 42, 0.08);
}

.nset__rest-card:focus-within {
  outline: 2px solid var(--color-accent);
  outline-offset: 2px;
}

.nset__rest-icon {
  width: 28px;
  height: 28px;
  color: var(--color-textMuted);
  flex-shrink: 0;
}

.nset__rest-card--active .nset__rest-icon {
  color: var(--color-accent);
}

.nset__rest-name {
  font-size: 14px;
  font-weight: 600;
  color: var(--color-text);
}

.nset__rest-desc {
  font-size: 12px;
  color: var(--color-textMuted);
  line-height: 1.4;
}

/* ── Content-mode options (step 3) ── */
.nset__option-group {
  display: flex;
  flex-direction: column;
  gap: var(--space-3);
}

.nset__option-card {
  display: flex;
  align-items: center;
  gap: var(--space-4);
  padding: var(--space-4) var(--space-6);
  background: var(--color-surface2);
  border: 1px solid var(--color-border);
  border-radius: var(--radius-md);
  cursor: pointer;
  transition: border-color var(--duration-base) var(--easing-default),
              background var(--duration-base) var(--easing-default);
}

.nset__option-card--active {
  border-color: var(--color-accent);
  background: rgba(225, 29, 42, 0.08);
}

.nset__option-card:focus-within {
  outline: 2px solid var(--color-accent);
  outline-offset: 2px;
}

.nset__option-icon {
  width: 28px;
  height: 28px;
  flex-shrink: 0;
  color: var(--color-textMuted);
}

.nset__option-card--active .nset__option-icon {
  color: var(--color-accent);
}

.nset__option-label {
  font-size: 15px;
  font-weight: 500;
  color: var(--color-text);
  flex: 1;
}

/* ── PIN ── */
.nset__pin-group {
  display: flex;
  flex-direction: column;
  gap: var(--space-3);
}

.nset__pin-label {
  font-size: 12px;
  font-weight: 600;
  color: var(--color-textMuted);
  text-transform: uppercase;
  letter-spacing: 0.3px;
}

.nset__pin-row {
  display: flex;
  gap: var(--space-3);
}

.nset__pin-cell {
  width: 56px;
  height: 64px;
  border-radius: var(--radius-sm);
  background: var(--color-surface2);
  border: 1px solid var(--color-border);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 28px;
  font-weight: 700;
  color: var(--color-text);
  font-family: var(--font-mono);
  transition: border-color var(--duration-fast) var(--easing-default);
}

.nset__pin-cell--filled {
  border-color: var(--color-text);
}

.nset__pin-hidden {
  height: 48px;
  width: 100%;
  padding-inline: 14px;
  background: var(--color-surface2);
  border: 1px solid var(--color-border);
  border-radius: var(--radius-sm);
  color: var(--color-text);
  font-size: 15px;
  font-family: var(--font-hebrew);
  outline: none;
  transition: border-color var(--duration-fast) var(--easing-default),
              box-shadow var(--duration-fast) var(--easing-default);
}

.nset__pin-hidden:focus {
  border-color: var(--color-accent);
  box-shadow: 0 0 0 3px rgba(225, 29, 42, 0.2);
}

.nset__pin-hint {
  font-size: 12px;
  color: var(--color-textDim);
  margin: 0;
}

/* ── Actions row ── */
.nset__actions {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: var(--space-3);
  border-block-start: 1px solid var(--color-border);
  padding-block-start: var(--space-4);
}

.nset__actions--end {
  justify-content: flex-end;
}

/* ── Inline alert banner ── */
.nset__banner {
  display: flex;
  align-items: center;
  gap: var(--space-3);
  padding: 14px 20px;
  border-radius: var(--radius-md);
  font-size: 14px;
  margin-block-end: var(--space-6);
  max-width: 560px;
  margin-inline: auto;
}

.nset__banner--error {
  background: rgba(224, 65, 65, 0.12);
  border: 1px solid rgba(224, 65, 65, 0.33);
  color: var(--color-danger);
}

/* ── Shared hidden radio ── */
.nset__radio-native {
  position: absolute;
  opacity: 0;
  width: 0;
  height: 0;
  pointer-events: none;
}

/* ── Reduced motion ── */
@media (prefers-reduced-motion: reduce) {
  .nset__step-dot,
  .nset__rest-card,
  .nset__option-card,
  .nset__pin-cell,
  .nset__pin-hidden {
    transition: none;
  }
}
</style>
